import React, { useEffect, useMemo } from "react";
import { Card } from "./Card";
import LoadingSkeleton from "./LoadingSkeleton";
import ResponsiveTruncatedText from "./ResponsiveTruncatedText";
import { useUrlMetadata } from "hooks/useUrlMetadata";
import { extractNestedUrl } from "util/fetchUrlMetadata";
import { getFaviconUrl } from "util/faviconUtils";
import { apiRoutes } from "util/apiRoutes";

// Strict and explicit prop types for robustness
export interface UrlPreviewCardProps {
  /**
   * The raw URL string pasted/typed by the user.
   * May contain nested trackers or multiple https occurrences; we will attempt
   * to extract the final, most relevant URL safely.
   */
  url: string;
  /**
   * Optional className to style the outer `Card` container.
   */
  className?: string;
  /**
   * If true, fetches metadata immediately on mount. Default: true
   */
  autoFetch?: boolean;
}

/**
 * UrlPreviewCard
 *
 * Displays a compact, responsive preview tile for a given URL.
 * - Image (if any) on the left
 * - Title (if any) and the canonical URL on the right
 *
 * Security:
 * - Links open with target="_blank" and rel="noopener noreferrer"
 * - No dynamic HTML injection; all text rendered safely
 */
const UrlPreviewCard: React.FC<UrlPreviewCardProps> = ({ url, className, autoFetch = true }) => {
  const { meta, loading, setMeta, fetchNow, fetchDebounced } = useUrlMetadata();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Derive a canonical URL to use for fetching and for display.
  const canonicalUrl: string | null = useMemo(() => {
    if (!url || typeof url !== "string") return null;
    const trimmed = url.trim();
    if (trimmed.length === 0) return null;
    const nested = extractNestedUrl(trimmed);
    return nested ?? trimmed;
  }, [url]);

  // Kick off metadata retrieval when url changes
  useEffect(() => {
    if (!canonicalUrl) {
      setMeta(null);
      return;
    }
    if (autoFetch) {
      // Use debounced fetch to avoid rapid calls while the user is typing
      fetchDebounced(canonicalUrl);
    }
  }, [canonicalUrl, autoFetch, fetchDebounced, setMeta]);

  // If autoFetch is disabled, still allow manual immediate fetch via effect once.
  useEffect(() => {
    if (!canonicalUrl || autoFetch) return;
    fetchNow(canonicalUrl).catch(() => undefined);
  }, [canonicalUrl, autoFetch, fetchNow]);

  const displayTitle: string | undefined = meta?.title && meta.title.trim().length > 0 ? meta.title : undefined;
  const displayUrl: string = canonicalUrl ?? "";

  // Try to normalize various shapes for og:image
  const deriveImageUrl = (): string | undefined => {
    const value = meta?.image as unknown;
    const normalize = (candidate: string | undefined): string | undefined => {
      if (!candidate) return undefined;
      try {
        const absolute = candidate.startsWith("//") ? `https:${candidate}` : candidate;
        return canonicalUrl ? new URL(absolute, canonicalUrl).toString() : new URL(absolute).toString();
      } catch {
        return undefined;
      }
    };

    if (typeof value === "string") return normalize(value);
    if (Array.isArray(value)) {
      const firstWithUrl = value.find((v) => typeof v === "object" && v !== null && (v as { url?: string }).url);
      return normalize((firstWithUrl as { url?: string } | undefined)?.url);
    }
    if (typeof value === "object" && value !== null) {
      return normalize((value as { url?: string }).url);
    }
    return undefined;
  };

  const rawImageUrl = deriveImageUrl();
  const imageUrl: string | undefined = rawImageUrl
    ? `${apiUrl}${apiRoutes.utilities.imageProxy}?imageUrl=${encodeURIComponent(rawImageUrl)}`
    : undefined;

  // Favicon support shown before site name
  const [faviconSrc, setFaviconSrc] = React.useState<string | null>(null);
  useEffect(() => {
    if (!canonicalUrl) {
      setFaviconSrc(null);
      return;
    }
    let isMounted = true;
    getFaviconUrl(canonicalUrl)
      .then((src) => {
        if (isMounted) setFaviconSrc(src);
      })
      .catch(() => {
        if (isMounted) setFaviconSrc(null);
      });
    return () => {
      isMounted = false;
    };
  }, [canonicalUrl]);

  return (
    <Card
      padding="sm"
      className={`w-full overflow-hidden ${className ?? ""}`}
      aria-busy={loading ? "true" : "false"}
      aria-live="polite"
    >
      <a
        href={displayUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-stretch gap-3 no-underline hover:bg-eoe-bg-card/60 dark:hover:bg-eoe-dark-bg-card/60 transition-colors"
      >
        {/* Left media - hidden if no image and not loading */}
        {(loading || imageUrl) && (
          <div className="shrink-0 w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-md overflow-hidden bg-eoe-input dark:bg-eoe-dark-input border border-eoe-border dark:border-eoe-dark-border">
            {loading ? (
              <LoadingSkeleton count={1} height="h-full" width="w-full" margin="m-0" rounded="rounded-md" />
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={displayTitle ?? "Link preview image"}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
                loading="lazy"
              />
            ) : null}
          </div>
        )}

        {/* Right text column */}
        <div className="min-w-0 flex-1 py-1 pr-2 flex flex-col justify-center">
          {displayTitle ? (
            <div className="text-sm sm:text-base font-semibold text-foreground leading-snug line-clamp-2">
              {displayTitle}
            </div>
          ) : null}

          <ResponsiveTruncatedText
            text={displayUrl}
            className="mt-1 text-xs sm:text-sm text-blue-600 dark:text-blue-400 hover:underline"
          />

          {meta?.siteName ? (
            <div className="mt-1 text-[11px] sm:text-xs text-muted-foreground flex items-center gap-1">
              {faviconSrc ? (
                <img src={faviconSrc} alt="Site icon" className="size-3.5 rounded-sm" loading="lazy" />
              ) : null}
              {meta.siteName}
            </div>
          ) : null}
        </div>
      </a>
    </Card>
  );
};

export default UrlPreviewCard;


